import React, { useState, useEffect } from "react";
import { X, Plus, Package, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { errMsg } from "../lib/api";
import SearchableSelect from "./ui/SearchableSelect";

// Owner rule (20 Oct 2026): these four are the only product types.
const CATEGORIES = ["Retail", "Technical", "Disposable", "Electrical"];
const QTY_UNITS = ["pcs", "grm", "ml"];
const STOCK_UNITS = ["bottle", "packet", "box", "pcs"];
const normQtyUnit = (u) => {
  const v = (u || "").toLowerCase();
  if (["piece", "pcs", "pc"].includes(v)) return "pcs";
  if (["gram", "gm", "grm", "gms"].includes(v)) return "grm";
  if (["ml", "millilitre", "milliliter"].includes(v)) return "ml";
  if (["ltr", "litre", "liter", "l"].includes(v)) return "ltr";
  if (["kg", "kgs"].includes(v)) return "kg";
  return v || "pcs";
};
const normStockUnit = (u) => {
  const v = (u || "").toLowerCase();
  if (["bottle", "bottles"].includes(v)) return "bottle";
  if (["packet", "packets", "pkt"].includes(v)) return "packet";
  if (["box", "boxes"].includes(v)) return "box";
  if (["piece", "pieces", "pcs", "pc"].includes(v)) return "pcs";
  return v || "";
};

export default function AddProductModal({ products = [], onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Retail");
  const [ledger] = useState("retail"); // field hidden from the form; payload keeps the retail default
  const [brand, setBrand] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [unit, setUnit] = useState("pcs");
  const [stockUnit, setStockUnit] = useState("");
  const [qty, setQty] = useState(0);
  const [unitPrice, setUnitPrice] = useState(0);
  const [storeQty, setStoreQty] = useState(10);
  const [minStock, setMinStock] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [existingProducts, setExistingProducts] = useState(products || []);
  useEffect(() => {
    if (!products || products.length === 0) {
      api.get("/inventory/skus")
        .then(res => {
          setExistingProducts(res.data || []);
        })
        .catch(err => console.error(err));
    }
  }, [products]);

  const uniqueBrands = Array.from(new Set(existingProducts.map(p => p.brand).filter(Boolean)));
  const uniqueVendors = Array.from(new Set(existingProducts.map(p => p.vendor_name || p.vendor).filter(Boolean)));


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a product name");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/inventory/product", {
        name: name.trim(),
        category,
        ledger,
        brand: brand.trim(),
        vendor_name: vendorName.trim(),
        unit,
        stock_unit: stockUnit,
        qty: parseFloat(qty) || 0,
        unit_price: parseFloat(unitPrice) || 0,
        store_qty: parseFloat(storeQty) || 0,
        min_stock: isNaN(parseFloat(minStock)) ? 0 : parseFloat(minStock),
      });

      toast.success(`Successfully added item '${res.data.name}'!`);
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in duration-200" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white border border-slate-200 shadow-2xl rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col text-slate-950 overflow-hidden"
       onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-6 text-white flex items-center justify-between border-b border-amber-500/30 shrink-0">
          <div>
            <div className="lss-overline text-amber-300">Inventory Master Entry</div>
            <h3 className="font-serif-lux text-2xl font-bold tracking-tight text-white mt-0.5">
              Add New Inventory Item
            </h3>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">
                Product Name <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g. L'Oreal Paris Professional Shampoo 500ml"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="lss-input w-full text-xs font-bold"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Category</label>
              <SearchableSelect
                options={CATEGORIES}
                value={category}
                onChange={(val) => setCategory(val)}
                placeholder="Select Category"
                getOptionLabel={(c) => c}
                getOptionValue={(c) => c}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Qty Unit (pack size)</label>
              <SearchableSelect
                options={QTY_UNITS}
                value={unit}
                onChange={(val) => setUnit(val)}
                placeholder="Select Unit"
                getOptionLabel={(u) => u}
                getOptionValue={(u) => u}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Stock Unit (bottle / piece)</label>
              <SearchableSelect
                options={STOCK_UNITS}
                value={stockUnit}
                onChange={(val) => setStockUnit(val)}
                placeholder="Select Unit"
                getOptionLabel={(u) => u}
                getOptionValue={(u) => u}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Brand</label>
              <input
                type="text"
                placeholder="e.g. L'Oreal, Schwarzkopf, Kerastase"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="lss-input w-full text-xs font-semibold"
                list="add-product-brands"
              />
              <datalist id="add-product-brands">
                {uniqueBrands.map(b => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Vendor Name</label>
              <input
                type="text"
                placeholder="e.g. Luxury Salon Distributors"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                className="lss-input w-full text-xs font-semibold"
                list="add-product-vendors"
              />
              <datalist id="add-product-vendors">
                {uniqueVendors.map(v => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
            <span className="lss-overline text-amber-900">Pricing & Initial Stock</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Unit Price (₹) — drives valuation</label>
                <input
                  type="number"
                  step="any"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  className="lss-input w-full font-bold tabular"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 block mb-1">Qty per Unit (size)</label>
                <input
                  type="number"
                  step="any"
                  value={qty}
                  placeholder="e.g. 500 (ml), 10 (boxes)"
                  onChange={(e) => setQty(e.target.value)}
                  className="lss-input w-full font-bold tabular"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 block mb-1">Opening Stock</label>
                <input
                  type="number"
                  value={storeQty}
                  onChange={(e) => setStoreQty(e.target.value)}
                  className="lss-input w-full font-bold tabular"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 block mb-1">Min Stock Alert</label>
                <input
                  type="number"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  className="lss-input w-full font-bold tabular"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="lss-btn-outline px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="lss-btn-gold px-6 py-2.5 text-xs font-extrabold uppercase tracking-wider shadow-md disabled:opacity-50 flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              <span>{submitting ? "Adding..." : "Add Product Item"}</span>
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
